// client/src/pages/admin/AdminCategories.jsx

import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import Swal from 'sweetalert2';

import { API_BASE, buildAssetUrl } from '../../utils/url';

const AdminCategories = () => {
  // ─── State ──────────────────────────────────────────────────────────────────
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal controls:
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);

  // Form data:
  //  - name: text
  //  - image: File object (if user picks one)
  //  - imagePreview: string URL for existing or newly selected image
  const [formData, setFormData] = useState({
    name: '',
    image: null,
    imagePreview: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // ─── On Mount: fetch categories ──────────────────────────────────────────────
  useEffect(() => {
    fetchCategories();
  }, []);

  // ─── Fetch All Categories ───────────────────────────────────────────────────
  // We expect each category to have: { id, name, image_path, … }
  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get('/categories');
      // data → [ { id, name, image_path, products: [...]? } ]
      setCategories(data);
    } catch (err) {
      console.error('Error loading categories:', err);
      Swal.fire('Error', 'Failed to load categories', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Open “Add New Category” Modal ───────────────────────────────────────────
  const openAddModal = () => {
    setEditMode(false);
    setCurrentCategory(null);
    setFormData({
      name: '',
      image: null,
      imagePreview: ''
    });
    setIsModalOpen(true);
  };

  // ─── Open “Edit Category” Modal ──────────────────────────────────────────────
  const openEditModal = (category) => {
    setEditMode(true);
    setCurrentCategory(category);

    // Pre‐fill the form with existing name + imagePreview
    const previewUrl = buildAssetUrl(category.image_path);

    setFormData({
      name: category.name || '',
      image: null,
      imagePreview: previewUrl
    });
    setIsModalOpen(true);
  };

  // ─── Close Modal & Reset ─────────────────────────────────────────────────────
  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentCategory(null);
    setFormData({ name: '', image: null, imagePreview: '' });
  };

  // ─── Handle Name Change ──────────────────────────────────────────────────────
  const handleNameChange = (e) => {
    setFormData(fd => ({
      ...fd,
      name: e.target.value
    }));
  };

  // ─── Handle File Selection ───────────────────────────────────────────────────
  const handleImageChange = (e) => {
    if (!e.target.files?.[0]) return;
    const file = e.target.files[0];
    // Create a local URL for preview
    const preview = URL.createObjectURL(file);
    setFormData(fd => ({
      ...fd,
      image: file,
      imagePreview: preview
    }));
  };

  // ─── Utility: Upload One File ─────────────────────────────────────────────────
  // Returns a string path, e.g. "uploads/categories/1623158234-foo.jpg"
  const uploadSingleFile = async (file) => {
    const uploadForm = new FormData();
    uploadForm.append('file', file);
    // Make sure our server’s multer endpoint is listening on /upload/file
    const { data } = await api.post('/upload/file', uploadForm, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    // data → { path: "uploads/categories/xxxxx.jpg" }
    return data.path;
  };

  // ─── Handle Form Submit (Add OR Edit) ────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      return Swal.fire('Validation Error', 'Category name is required', 'error');
    }

    setIsSubmitting(true);
    try {
      let finalImagePath = editMode && currentCategory
        ? currentCategory.image_path
        : '';

      // If user selected a new file, upload it first:
      if (formData.image) {
        const uploadedPath = await uploadSingleFile(formData.image);
        finalImagePath = uploadedPath; // e.g. "uploads/categories/1623158234-foo.jpg"
      }

      // Build payload exactly as the server expects:
      const payload = {
        name: formData.name.trim(),
        image_path: finalImagePath || ''
      };

      if (editMode && currentCategory) {
        // EDIT existing:
        await api.put(`/categories/${currentCategory.id}`, payload);
        Swal.fire({
          icon: 'success',
          title: 'Category updated successfully',
          timer: 1200,
          showConfirmButton: false
        });
      } else {
        // CREATE new:
        await api.post('/categories', payload);
        Swal.fire({
          icon: 'success',
          title: 'Category created successfully',
          timer: 1200,
          showConfirmButton: false
        });
      }

      closeModal();
      fetchCategories();
    } catch (err) {
      console.error('Error saving category:', err);
      Swal.fire(
        'Error',
        err.response?.data?.message || 'Failed to save category',
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Handle Delete Category ──────────────────────────────────────────────────
  const handleDelete = (category) => {
    Swal.fire({
      title: `Delete “${category.name}”?`,
      text: 'This will permanently remove the category.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          await api.delete(`/categories/${category.id}`);
          Swal.fire({
            icon: 'success',
            title: 'Deleted',
            timer: 1200,
            showConfirmButton: false
          });
          fetchCategories();
        } catch (err) {
          console.error('Error deleting category:', err);
          Swal.fire('Error', err.response?.data?.message || 'Failed to delete category', 'error');
        }
      }
    });
  };

  // ─── Handle Delete All Categories ────────────────────────────────────────────
  const handleDeleteAll = () => {
    if (categories.length === 0) {
      return Swal.fire('Info', 'No categories to delete', 'info');
    }

    Swal.fire({
      title: 'Delete All Categories?',
      text: `This will permanently remove all ${categories.length} categories. This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete all!',
      cancelButtonText: 'Cancel'
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          // Show loading
          Swal.fire({
            title: 'Deleting...',
            text: 'Please wait while we delete all categories',
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            }
          });

          // Delete all categories one by one
          const deletePromises = categories.map(cat =>
            api.delete(`/categories/${cat.id}`)
          );

          await Promise.all(deletePromises);

          Swal.fire({
            icon: 'success',
            title: 'All Categories Deleted',
            text: `Successfully deleted ${categories.length} categories`,
            timer: 2000,
            showConfirmButton: false
          });

          fetchCategories();
        } catch (err) {
          console.error('Error deleting all categories:', err);
          Swal.fire('Error', err.response?.data?.message || 'Failed to delete all categories', 'error');
        }
      }
    });
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-6">
      {/* Header Bar */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Categories</h1>
        <div className="flex gap-3">
          <button
            onClick={handleDeleteAll}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition"
            disabled={categories.length === 0}
            style={{
              opacity: categories.length === 0 ? 0.5 : 1,
              cursor: categories.length === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            Delete All
          </button>
          <button
            onClick={openAddModal}
            className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition"
          >
            Add New Category
          </button>
        </div>
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
        </div>
      ) : (
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          {categories.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse'
              }}>
                <thead>
                  <tr style={{
                    background: '#f8f9fa',
                    borderBottom: '1px solid #e9ecef'
                  }}>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>
                      Image
                    </th>
                    <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>
                      Name
                    </th>
                    <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600' }}>
                      Products
                    </th>
                    <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map(cat => (
                    <tr key={cat.id} style={{
                      borderBottom: '1px solid #f1f3f4'
                    }}>
                      {/* Image Column: render thumbnail if available */}
                      <td style={{ padding: '16px' }}>
                        {cat.image_path ? (
                          <img
                            src={buildAssetUrl(cat.image_path)}
                            alt={cat.name}
                            style={{ width: '48px', height: '48px', borderRadius: '8px', objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{ width: '48px', height: '48px', background: '#f3f4f6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ color: '#6b7280', fontSize: '10px' }}>No image</span>
                          </div>
                        )}
                      </td>

                      {/* Name Column */}
                      <td style={{ padding: '16px' }}>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>{cat.name}</div>
                      </td>

                      {/* Products Count Column */}
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {cat.products?.length ?? 0} products
                        </div>
                      </td>

                      {/* Actions Column */}
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <button
                            onClick={() => openEditModal(cat)}
                            style={{
                              background: '#3b82f6',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(cat)}
                            style={{
                              background: '#ef4444',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">No categories found</p>
              <button
                onClick={openAddModal}
                className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
              >
                Add Your First Category
              </button>
            </div>
          )}
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────────────
            Add / Edit Category Modal
         ──────────────────────────────────────────────────────────────────── */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">
              {editMode ? 'Edit Category' : 'Add New Category'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleNameChange}
                  className="w-full px-3 py-2 border rounded focus:ring-black focus:border-black"
                  placeholder="Category name"
                  required
                />
              </div>

              {/* Image Preview + File Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image
                </label>
                <div className="flex items-center space-x-4">
                  {formData.imagePreview ? (
                    <img
                      src={formData.imagePreview}
                      alt="Preview"
                      className="h-16 w-16 rounded object-cover"
                    />
                  ) : (
                    <div className="h-16 w-16 bg-gray-200 rounded flex items-center justify-center">
                      <span className="text-gray-500 text-xs">No image</span>
                    </div>
                  )}
                  <input
                    type="file"
                    name="image"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? editMode
                      ? 'Saving...'
                      : 'Creating...'
                    : editMode
                      ? 'Update'
                      : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
